/** Sidebar/teleport actions wired in by main once the world exists. */
export interface HudActions {
  onHome: () => void;
  onDirectory: () => void;
  onCart: () => void;
  onGiving: () => void;
  /** Category chips along the bottom; each teleports to a destination. */
  categories: Array<{ label: string; onClick: () => void }>;
}

/** Icon shapes as [tag, attributes] pairs, rendered via createElementNS. */
type IconShape = [string, Record<string, string>];
const ICONS: Record<string, IconShape[]> = {
  home: [
    ["path", { d: "M3 10.5 12 3l9 7.5" }],
    ["path", { d: "M5 9.5V21h5v-6h4v6h5V9.5" }],
  ],
  directory: [
    ["rect", { x: "3", y: "3", width: "7", height: "7", rx: "1" }],
    ["rect", { x: "14", y: "3", width: "7", height: "7", rx: "1" }],
    ["rect", { x: "3", y: "14", width: "7", height: "7", rx: "1" }],
    ["rect", { x: "14", y: "14", width: "7", height: "7", rx: "1" }],
  ],
  cart: [
    ["circle", { cx: "9", cy: "20", r: "1.4" }],
    ["circle", { cx: "17", cy: "20", r: "1.4" }],
    ["path", { d: "M3 4h2l2.4 12h10.2l2.4-9H6" }],
  ],
  giving: [
    ["path", { d: "M12 20.5S4 15 4 9.5A4.5 4.5 0 0 1 12 6a4.5 4.5 0 0 1 8 3.5c0 5.5-8 11-8 11z" }],
  ],
};

const SVG_NS: string = "http://www.w3.org/2000/svg";

function buildIcon(name: string): SVGSVGElement {
  const svg: SVGSVGElement = document.createElementNS(SVG_NS, "svg") as SVGSVGElement;
  svg.setAttribute("viewBox", "0 0 24 24");
  svg.setAttribute("aria-hidden", "true");
  for (const [tag, attrs] of ICONS[name]) {
    const el: Element = document.createElementNS(SVG_NS, tag);
    for (const [k, v] of Object.entries(attrs)) {
      el.setAttribute(k, v);
    }
    svg.appendChild(el);
  }
  return svg;
}

/**
 * Screen chrome in the reference-hub style: a status pill up top, a dark
 * glass icon sidebar on the left, a player card top right, and category
 * chips along the bottom. All content is DOM; the 3D canvas sits behind.
 */
export class HUD {
  private el: HTMLElement;
  private statusLine: string = "";
  private cartCount: number = 0;
  private cartTotal: string = "";
  private onCartClick: (() => void) | undefined;
  private cartBadge: HTMLElement | undefined;
  private playerSub: HTMLElement | undefined;

  constructor(elementId: string) {
    const found: HTMLElement | null = document.getElementById(elementId);
    if (found === null) {
      throw new Error(`HUD element #${elementId} not found`);
    }
    this.el = found;
  }

  setStatus(text: string): void {
    this.statusLine = text;
    this.render();
  }

  setCart(count: number, total: string): void {
    this.cartCount = count;
    this.cartTotal = total;
    this.render();
  }

  setOnCartClick(handler: () => void): void {
    this.onCartClick = handler;
    this.render();
  }

  /** Show the top-right player card once the username is known. Signed-in
   *  players (onSignOut provided) get a "Sign out" button on the card. */
  setPlayer(name: string, onSignOut?: () => void): void {
    const card: HTMLElement | null = document.getElementById("player-card");
    if (card === null) {
      return;
    }
    card.hidden = false;
    card.replaceChildren();
    const avatar: HTMLDivElement = document.createElement("div");
    avatar.className = "pc-avatar";
    avatar.textContent = name.charAt(0).toUpperCase();
    const text: HTMLDivElement = document.createElement("div");
    const nameEl: HTMLDivElement = document.createElement("div");
    nameEl.className = "pc-name";
    nameEl.textContent = name;
    const sub: HTMLDivElement = document.createElement("div");
    sub.className = "pc-sub";
    sub.textContent = onSignOut !== undefined ? "signed in" : "browsing as guest";
    text.appendChild(nameEl);
    text.appendChild(sub);
    card.appendChild(avatar);
    card.appendChild(text);
    if (onSignOut !== undefined) {
      const out: HTMLButtonElement = document.createElement("button");
      out.className = "pc-signout";
      out.type = "button";
      out.textContent = "Sign out";
      out.addEventListener("click", onSignOut);
      card.appendChild(out);
    }
    this.playerSub = sub;
    this.render();
  }

  /** Build the sidebar and category chips once the world/actions exist. */
  installChrome(actions: HudActions): void {
    const sidebar: HTMLElement | null = document.getElementById("sidebar");
    if (sidebar !== null) {
      sidebar.replaceChildren();
      const entries: Array<{ icon: string; label: string; onClick: () => void; badged?: boolean }> = [
        { icon: "home", label: "Home", onClick: actions.onHome },
        { icon: "directory", label: "Stores", onClick: actions.onDirectory },
        { icon: "cart", label: "Cart", onClick: actions.onCart, badged: true },
        { icon: "giving", label: "Giving", onClick: actions.onGiving },
      ];
      for (const entry of entries) {
        const btn: HTMLButtonElement = document.createElement("button");
        btn.className = "sb-btn";
        btn.type = "button";
        btn.appendChild(buildIcon(entry.icon));
        const label: HTMLSpanElement = document.createElement("span");
        label.textContent = entry.label;
        btn.appendChild(label);
        if (entry.badged === true) {
          const badge: HTMLSpanElement = document.createElement("span");
          badge.className = "sb-badge";
          badge.hidden = true;
          btn.appendChild(badge);
          this.cartBadge = badge;
        }
        btn.addEventListener("click", entry.onClick);
        sidebar.appendChild(btn);
      }
    }

    const bar: HTMLElement | null = document.getElementById("category-bar");
    if (bar !== null && actions.categories.length > 0) {
      bar.replaceChildren();
      bar.hidden = false;
      for (const cat of actions.categories) {
        const chip: HTMLButtonElement = document.createElement("button");
        chip.className = "cb-chip";
        chip.type = "button";
        chip.textContent = cat.label;
        chip.addEventListener("click", cat.onClick);
        bar.appendChild(chip);
      }
    }
    this.render();
  }

  private render(): void {
    this.el.replaceChildren();
    this.el.appendChild(document.createTextNode(this.statusLine));
    if (this.cartCount > 0) {
      this.el.appendChild(document.createTextNode("  —  "));
      const cart: HTMLSpanElement = document.createElement("span");
      cart.className = "hud-cart";
      cart.textContent = `🛒 ${this.cartCount} item${this.cartCount === 1 ? "" : "s"} • ${this.cartTotal}`;
      if (this.onCartClick !== undefined) {
        const handler = this.onCartClick;
        cart.addEventListener("click", () => handler());
      }
      this.el.appendChild(cart);
    }
    if (this.cartBadge !== undefined) {
      this.cartBadge.hidden = this.cartCount === 0;
      this.cartBadge.textContent = String(this.cartCount);
    }
    if (this.playerSub !== undefined) {
      this.playerSub.textContent =
        this.cartCount > 0 ? `${this.cartCount} in cart • ${this.cartTotal}` : "browsing the mall";
    }
  }
}
